import loadable from '@loadable/component';
import type { RouteConfig } from '../../packages/router';
import { isPreStage, isProduction } from './modules/env';
import { AUTH_MESSAGES } from './modules/user/constants';
import {
  BOOKING_ROUTE,
  BOOKING_SUCCESS_ROUTE,
  CLIENT_PAYMENT_METHOD_ADD,
  CLIENT_PAYMENT_METHOD_DETAIL,
  CLIENT_PAYMENT_METHOD_LIST,
  CLIENT_PAYMENT_METHOD_PARENT,
  PROFILE_EDIT_ROUTE,
  PROFILE_EDIT_SERVICE_INFO_ROUTE,
  PROFILE_ROUTE,
  PROVIDER_SEARCH_RANK,
  PROVIDER_SEARCH_RANK_NEW_CLIENTS,
  PROVIDER_SEARCH_RANK_PAYMENTS,
  SALON_ROUTE,
  ADJUST_TIP,
  ADJUST_TIP_THANK_YOU,
  ADJUST_TIP_DEFAULT,
  ADJUST_TIP_REFUNDED,
  CLIENT_ACCOUNT_SETTINGS_ROUTE,
  CLIENT_CONTACT_INFORMATION_ROUTE,
  CLIENT_PROMOTIONS_LIST_ROUTE,
  CLIENT_CHANGE_PASSWORD_ROUTE,
  CLIENT_DEFAULT_TIP_SETTING_ROUTE,
  CLIENT_APPTS_LIST,
  CLIENT_APPTS_LIST_DEFAULT,
  CLIENT_APPTS_LIST_FUTURE,
  PROVIDER_CLIENT_NOTE,
  CLIENT_FILL_SCHEDULE_GAP_ROUTE,
  PRO_PERSONAL_APPOINTMENT_VIEW,
  PRO_PERSONAL_APPOINTMENT_EDIT,
  PRO_CALENDAR_REFUND_APPOINTMENT,
  TRACKED_CLIENT_SIGNUP,
  CLIENT_PAYMENT_REQUEST_CHECKOUT,
  CLIENT_INVITED_TO_BOOK,
  USER_DEPENDENT_LEGAL,
  USER_INDEPENDENT_LEGAL,
  PRO_CLIENT_IMPORT,
  PROVIDER_EDIT_REVIEWS,
  CHAT,
  PRO_PERSONAL_INFO_SETTINGS,
  PRO_CLIENT_NOTIFICATION_SETTINGS,
  PROVIDER_SETTINGS_SOCIAL,
  PRO_ADJUST_UPCOMING_HOURS,
  PRO_TAPTOPAY_ONBOARD_SETTINGS,
  PRO_TAPTOPAY_ONBOARD_SETTINGS_DEEP_LINK,
  PRO_BUSINESS_INFO_SETTINGS,
  PRO_MANAGE_VACATION,
  PRO_CLIENT_LIST,
  PRO_CLIENT_VIEW_APPOINTMENTS,
  PRO_CALENDAR_NEW_APPOINTMENT,
  CALENDAR_ROUTE,
  PRO_MANAGE_ONGOING_SCHEDULE,
  PRO_ADD_SERVICES_AND_CATEGORIES,
  PRO_CALENDAR_VIEW_APPOINTMENT,
  PRO_CALENDAR_EDIT_APPOINTMENT,
  PRO_CLIENT_VIEW,
  PRO_CLIENT_VIEW_APPOINTMENT,
  PRO_IMAGE_LIST,
  EXPIRED_APP_BLOCKER_ROUTE,
  PROVIDER_SETTINGS_ONLINE_BOOKING_MENU,
  PROVIDER_SETTINGS_ONLINE_BOOKING,
  PRINTABLE_CALENDAR_ROUTE,
  PROVIDER_FEES,
} from './route-names';
import ChatPage from './components/pages/Chat/ChatPage';

export type RouteGuardConfig = {
  /**
   * True if a user must be logged in to view this route
   */
  loginRequired?: boolean;
  /**
   * The route state name to redirect to if user does not authenticate
   */
  loginFailedRedirect?: string; // should this support name / params, too?
  /**
   * The authentication origin to use for tracking
   */
  loginOrigin?: string;
  /**
   * A message to display to the user if logging in
   */
  loginMessage?: string;
};

export type StyleSeatRouteConfig = RouteConfig & RouteGuardConfig & {
  routes?: StyleSeatRouteConfig[];
};

export const routeConfigs: StyleSeatRouteConfig[] = [
  {
    stateName: EXPIRED_APP_BLOCKER_ROUTE,
    url: '/m/expired_app',
    component: loadable(() => import('./components/pages/ExpiredApp/ExpiredApp')),
  },
  {
    stateName: 'search',
    url: [
      '/m/search/:loc/:q',
      '/m/search//:q?',
    ],
    component: loadable(() => import('./components/pages/Search')),
  },
  {
    stateName: 'city_search_landing',
    url: ['/m/search/:loc'],
    component: loadable(() => import('./components/pages/CitySearch')),
  },
  {
    stateName: 'search_landing',
    url: ['/m/search'],
    component: loadable(() => import('./components/pages/Search')),
  },
  {
    stateName: 'home',
    exact: true,
    url: ['/m/'],
    data: { trackingName: 'search' },
    component: loadable(() => import('./components/pages/Home')),
  },
  {
    stateName: 'start',
    exact: true,
    url: [
      '/start',
      '/m/start',
    ],
    data: { trackingName: 'search' },
    component: loadable(() => import('./components/pages/Home')),
  },
  {
    stateName: 'profile_photo',
    url: '/m/p/:providerId/photo/:imageId',
    component: loadable(() => import('./components/pages/profile/Image')),
  },
  {
    stateName: 'provider_vanity_photo',
    url: '/m/v/:providerId/photo/:imageId',
    component: loadable(() => import('./components/pages/profile/Image')),
  },
  {
    stateName: PROFILE_EDIT_ROUTE,
    url: '/m/p/:providerId/edit',
    component: loadable(() => import('./components/pages/provider/profileEdit/ProfileEdit')),
    routes: [
      {
        stateName: PROFILE_EDIT_SERVICE_INFO_ROUTE,
        url: '/m/p/:providerId/edit/service/:serviceId',
        component: loadable(() => import('./components/pages/ServiceInfoEditPage')),
      },
    ],
  },
  {
    stateName: 'gallery',
    url: '/m/p/:providerId/gallery',
    component: loadable(() => import('./components/pages/profile/Gallery')),
  },
  {
    url: '/m/appointments',
    stateName: CLIENT_APPTS_LIST,
    data: { trackingName: 'appointments' },
    loginRequired: true,
    loginMessage: AUTH_MESSAGES.appointments,
    exact: true,
    component: loadable(() => import('./components/pages/ClientAppointmentsList/ClientAppointmentsListRouter')),
  },
  {
    url: '/m/chat/:chatId',
    stateName: CHAT,
    loginRequired: true,
    component: ChatPage,
  },
  {
    stateName: PROVIDER_EDIT_REVIEWS,
    url: '/m/provider/:providerId/reviews',
    component: loadable(() => import('./components/pages/provider/profileEdit/ProReviews')),
  },
  {
    stateName: PROVIDER_FEES,
    url: '/m/provider/:providerId/fees/:dateFilterStart/:dateFilterEnd',
    component: loadable(() => import('./components/pages/provider/payments/Fees')),
  },
  // Pro settings menu
  {
    stateName: PROVIDER_SETTINGS_ONLINE_BOOKING_MENU,
    url: '/m/provider/:providerId/settings_menu/online_booking',
    component: loadable(() => import('./components/AngularApp')),
    routes: [
      {
        stateName: PROVIDER_SETTINGS_ONLINE_BOOKING,
        url: '/m/provider/:providerId/settings_menu/online_booking/online_booking',
        component: loadable(() => import('./components/pages/provider/settings/OnlineBookingSettings')),
      },
    ],
  },
  // TODO: Create a settings parent here with individual children
  {
    stateName: PROVIDER_SETTINGS_SOCIAL,
    url: '/m/provider/:providerId/settings/social',
    component: loadable(() => import('./components/pages/provider/settings/SocialMedia')),
  },
  {
    stateName: PRO_PERSONAL_INFO_SETTINGS,
    url: '/provider/:providerId/settings/personal',
    component: loadable(() => import('./components/pages/provider/profileEdit/ProPersonalInfoPage')),
    data: {
      trackingName: 'P Settings Personal Info',
    },
  },
  {
    stateName: PRO_BUSINESS_INFO_SETTINGS,
    url: '/provider/:providerId/settings/salon',
    component: loadable(() => import('./components/pages/provider/profileEdit/ProBusinessInfoPage')),
    data: {
      trackingName: 'P Settings Salon Info',
    },
  },
  {
    stateName: PRO_ADJUST_UPCOMING_HOURS,
    url: '/m/provider/:providerId/availability/upcoming',
    component: loadable(() => import('./components/pages/provider/profileEdit/ProAdjustUpcomingHoursPage')),
  },
  {
    stateName: PRO_MANAGE_ONGOING_SCHEDULE,
    url: '/m/provider/:providerId/settings/availability',
    component: loadable(() => import('./components/pages/provider/settings/ProManageOngoingAvailabilityPage')),
  },
  {
    stateName: PRO_MANAGE_VACATION,
    url: '/m/provider/:providerId/availability/vacation',
    component: loadable(() => import('./components/pages/provider/profileEdit/ProManageVacationPage')),
  },
  {
    stateName: PRO_IMAGE_LIST,
    url: '/m/provider/:providerId/images',
    loginRequired: true,
    component: loadable(() => import('./components/pages/provider/profileEdit/ProImageManagement')),
  },
  {
    stateName: PRO_ADD_SERVICES_AND_CATEGORIES,
    url: '/m/provider/:providerId/services/add',
    component: loadable(() => import('./components/pages/provider/services/ManageAddServicesPage')),
  },
  {
    stateName: PRO_TAPTOPAY_ONBOARD_SETTINGS_DEEP_LINK,
    url: '/m/settings/tap-to-pay/setup',
    loginRequired: true,
    component: loadable(() => import('./components/pages/provider/settings/TapToPaySetupLandingPage')),
  },
  {
    stateName: PRO_TAPTOPAY_ONBOARD_SETTINGS,
    url: '/m/provider/:providerId/settings/tap-to-pay/setup',
    loginRequired: true,
    component: loadable(() => import('./components/pages/provider/settings/TapToPaySetupPage')),
  },
  {
    stateName: PRO_CLIENT_NOTIFICATION_SETTINGS,
    url: '/m/provider/:providerId/settings_menu/client-notification-settings',
    loginRequired: true,
    component: loadable(() => import('./components/pages/provider/settings/ProClientNotificationSettings')),
  },
  {
    url: '/m/appointments/list',
    stateName: CLIENT_APPTS_LIST_DEFAULT,
    data: { trackingName: 'appointments_list' },
    loginRequired: true,
    loginMessage: AUTH_MESSAGES.appointments,
    component: loadable(() => import('./components/pages/ClientAppointmentsList/ClientAppointmentsList')),
  },
  {
    url: '/m/appointments/future',
    stateName: CLIENT_APPTS_LIST_FUTURE,
    data: { trackingName: 'appointments_future' },
    loginRequired: true,
    loginMessage: AUTH_MESSAGES.appointments,
    component: loadable(() => import('./components/pages/ClientAppointmentsList/ClientAppointmentsListFuture')),
  },
  {
    url: '/m/appointment/:id/details',
    stateName: 'consumer.appointment_status_details',
    data: { trackingName: 'appointment_status_details' },
    component: loadable(() => import('./components/pages/AppointmentStatusDetailsPage')),
  },
  {
    url: '/m/appointment/:id/get_help',
    stateName: 'consumer.appointment_get_help',
    data: { trackingName: 'appointment_get_help' },
    component: loadable(() => import('./components/pages/AppointmentGetHelpPage')),
  },
  {
    url: '/m/appointment/:id/prepay',
    stateName: 'consumer.prepay_appointment',
    data: { trackingName: 'prepay_appt' },
    component: loadable(() => import('./components/pages/AppointmentPrepayPage')),
  },
  {
    url: '/m/guest_booking/:token?cancel',
    stateName: 'guest.landing',
    data: { trackingName: 'guest_booking' },
    component: loadable(() => import('./components/pages/GuestBookingManagementPage')),
  },
  {
    url: '/m/early_access/:token',
    stateName: CLIENT_FILL_SCHEDULE_GAP_ROUTE,
    component: loadable(() => import('./components/pages/FillScheduleGap')),
  },
  {
    stateName: CLIENT_ACCOUNT_SETTINGS_ROUTE,
    url: '/m/my-account',
    loginRequired: true,
    component: loadable(() => import('./components/pages/MyAccount/MyAccountPage')),
    routes: [
      {
        url: '/m/my-profile',
        stateName: CLIENT_CONTACT_INFORMATION_ROUTE,
        isChild: true,
        data: { trackingName: 'consumer_profile' },
        component: loadable(() => import('./components/pages/ConsumerProfilePage')),
      },
      {
        url: '/m/promotions/list',
        stateName: CLIENT_PROMOTIONS_LIST_ROUTE,
        data: { trackingName: 'promotions_list' },
        isChild: true,
        component: loadable(() => import('./components/pages/ClientPromotionsList')),
      },
      {
        url: '/m/change-password',
        stateName: CLIENT_CHANGE_PASSWORD_ROUTE,
        isChild: true,
        component: loadable(() => import('./components/pages/ChangePassword')),
      },
      {
        url: '/m/default-tip-edit',
        stateName: CLIENT_DEFAULT_TIP_SETTING_ROUTE,
        isChild: true,
        component: loadable(() => import('./components/pages/MyAccount/DefaultTipEditPage')),
      },
      {
        stateName: CLIENT_PAYMENT_METHOD_PARENT,
        url: '/m/user-credit-cards',
        isChild: true,
        component: loadable(() => import('./components/pages/client/paymentMethods/PaymentMethodParentPage')),
        loginRequired: true,
        loginMessage: AUTH_MESSAGES['update-payment'],
        routes: [
          {
            stateName: CLIENT_PAYMENT_METHOD_DETAIL,
            url: '/m/user-credit-cards/detail/:cardId',
            isChild: true,
            component: loadable(() => import('./components/pages/client/paymentMethods/PaymentMethodViewPage')),
          },
          {
            stateName: CLIENT_PAYMENT_METHOD_LIST,
            url: '/m/user-credit-cards/list',
            isChild: true,
            component: loadable(() => import('./components/pages/client/paymentMethods/PaymentMethodListPage')),
            routes: [
              {
                stateName: CLIENT_PAYMENT_METHOD_ADD,
                url: '/m/user-credit-cards/add',
                component: loadable(() => import('./components/pages/client/paymentMethods/PaymentMethodCreatePage')),
              },
            ],
          },
        ],
      },
    ],
  },
  {
    stateName: USER_DEPENDENT_LEGAL,
    url: ['/m/legal/:userType/:docType'],
    component: loadable(() => import('./components/pages/LegalPage')),
  },
  {
    stateName: USER_INDEPENDENT_LEGAL,
    url: ['/m/legal/:docType/'],
    component: loadable(() => import('./components/pages/LegalPage')),
  },
  {
    stateName: 'add_card_redirect',
    url: '/add-card',
    component: loadable(() => import('./components/pages/client/paymentMethods/PaymentMethodAddRedirectPage')),
  },
  {
    stateName: 'list_card_redirect',
    url: '/card-list',
    component: loadable(() => import('./components/pages/client/paymentMethods/PaymentMethodAddRedirectPage')),
  },
  {
    stateName: 'salon_photo',
    url: '/m/s/:providerId/photo/:imageId',
    component: loadable(() => import('./components/pages/profile/Image')),
  },
  {
    stateName: SALON_ROUTE,
    url: '/m/s/:salonId',
    component: loadable(() => import('./components/pages/Salon')),
  },
  {
    stateName: CLIENT_INVITED_TO_BOOK,
    url: '/m/invite',
    component: loadable(() => import('./components/pages/client/InvitedToBook')),
  },
  {
    stateName: PROFILE_ROUTE,
    url: [
      '/m/v/:providerId',
      '/m/p/:providerId',
    ],
    component: loadable(() => import('./components/pages/ProProfile')),
    routes: [
      {
        stateName: BOOKING_ROUTE,
        url: '/m/p/:providerId/booking/service/:serviceId/schedule',
        component: loadable(() => import('./components/pages/BookingPage')),
      },
      {
        stateName: BOOKING_SUCCESS_ROUTE,
        url: '/m/p/:providerId/booking/service/:serviceId/confirmation/:appointmentId',
        component: loadable(() => import('./components/pages/BookingSuccessPage')),
      },
      {
        stateName: 'profile.service_info',
        url: '/m/p/:providerId/service/:serviceId',
        component: loadable(() => import('./components/pages/ServiceInfoPage')),
      },
      {
        stateName: 'profile.share',
        url: '/m/p/:providerId/share',
        component: loadable(() => import('./components/AngularApp')),
      },
      {
        stateName: 'profile.message',
        url: '/m/p/:providerId/message-pro',
        component: loadable(() => import('./components/pages/profile/GuardedMessagePage')),
      },
    ],
  },
  {
    stateName: 'responsive.provider.promote',
    url: '/m/p/:providerId/promote?source',
    component: loadable(() => import('./components/pages/PromoteProfile/PromoteProfilePage')),
  },
  {
    stateName: PROVIDER_SEARCH_RANK,
    url: '/m/p/:providerId/search-rank',
    component: loadable(() => import('./components/pages/SearchRank')),
  },
  {
    stateName: PROVIDER_SEARCH_RANK_NEW_CLIENTS,
    url: '/m/p/:providerId/search-rank/new-clients',
    component: loadable(() => import('./components/pages/SearchRankNewClients')),
  },
  {
    stateName: PROVIDER_SEARCH_RANK_PAYMENTS,
    url: '/m/p/:providerId/search-rank/payments',
    component: loadable(() => import('./components/pages/SearchRankPayments')),
  },
  // Handle vanity URL rerouting
  {
    stateName: '404',
    url: '/m/404',
    exact: true,
    component: loadable(() => import('./components/AngularApp')),
  },
  {
    url: '/m/c/:discountCode',
    stateName: 'consumer.discount_enrollment',
    data: { trackingName: 'discount_enroll' },
    component: loadable(() => import('./components/pages/DiscountEnrollmentPage')),
  },
  {
    stateName: 'responsive.provider.promotions',
    url: '/m/provider/:providerId/promotions',
    component: loadable(() => import('./components/AngularApp')),
    routes: [
      {
        stateName: 'responsive.provider.promotions.view',
        url: '/m/provider/:providerId/promotions/:promotionId',
        component: loadable(() => import('./components/pages/provider/promotions/PromotionDetails')),
      },
    ],
  },
  {
    stateName: PRINTABLE_CALENDAR_ROUTE,
    url: '/printCalendar/:year/:month/:day/:mode/:providerId',
    component: loadable(() => import('./components/pages/provider/PrintCalendar/PrintCalendar')),
  },
  {
    stateName: CALENDAR_ROUTE,
    url: '/m/provider/:providerId/calendarNext/:year/:month/:day/:mode',
    component: loadable(() => import('./components/AngularApp')),
    loginRequired: true,
    routes: [
      {
        stateName: PRO_CALENDAR_NEW_APPOINTMENT,
        url: '/m/provider/:providerId/calendarNext/:year/:month/:day/:mode/new_appointment',
        component: loadable(() => import('./components/pages/provider/CreateAppointment/CreateAppointmentPage')),
        data: {
          trackingName: 'P Create Calendar Appointment',
        },
      },
      {
        stateName: PRO_PERSONAL_APPOINTMENT_VIEW,
        url: '/m/provider/:providerId/calendarNext/:year/:month/:day/:mode/personal-appointment/:viewApptId',
        data: {
          trackingName: 'P View Calendar Personal Appointment',
        },
        component: loadable(() => import('./components/pages/provider/PersonalAppointment/PersonalAppointmentViewPage')),
        loginRequired: true,
        routes: [
          {
            stateName: PRO_PERSONAL_APPOINTMENT_EDIT,
            url: '/m/provider/:providerId/calendarNext/:year/:month/:day/:mode/personal-appointment/:viewApptId/edit',
            data: {
              trackingName: 'P Edit Calendar Personal Appointment',
            },
            loginRequired: true,
            component: loadable(() => import('./components/pages/provider/PersonalAppointment/PersonalAppointmentEditPage')),
          } as StyleSeatRouteConfig,
        ],
      },
      {
        stateName: PRO_CALENDAR_VIEW_APPOINTMENT,
        url: '/m/provider/:providerId/calendarNext/:year/:month/:day/:mode/appointment/:viewApptId',
        component: loadable(() => import('./components/pages/provider/AppointmentDetails/AppointmentDetails')),
        routes: [
          {
            stateName: PRO_CALENDAR_EDIT_APPOINTMENT,
            url: '/m/provider/:providerId/calendarNext/:year/:month/:day/:mode/appointment/:viewApptId/edit',
            component: loadable(() => import('./components/pages/provider/AppointmentDetails/AppointmentDetailsEdit')),
          },
          {
            stateName: PRO_CALENDAR_REFUND_APPOINTMENT,
            url: '/m/provider/:providerId/calendarNext/:year/:month/:day/:mode/appointment/:viewApptId/refund',
            component: loadable(() => import('./components/provider/appointment/ApptRefund')),
          },
        ],
      },
    ],
  },
  {
    stateName: 'responsive.provider.account_password',
    url: ['/provider/:providerId/account/change-password'],
    data: { trackingName: 'Change Password' },
    component: loadable(() => import('./components/provider/settings/ChangePassword/ChangePasswordContainer')),
  },
  {
    url: '/m/feedback/pro/:vanityUrl',
    stateName: 'client.feedback.provider',
    component: loadable(() => import('./components/pages/ClientFeedback')),
  },
  {
    url: '/m/signupwall',
    stateName: 'signup_wall.main',
    component: loadable(() => import('./components/pages/WelcomeWall')),
  },
  {
    url: '/m/manage-appointment',
    stateName: 'consumer.manage_appointment',
    component: loadable(() => import('./components/pages/ClientManageAppointment')),
  },
  {
    url: '/m/more-options',
    stateName: 'consumer.more',
    component: loadable(() => import('./components/pages/ClientMore')),
  },
  {
    url: '/m/change-password',
    stateName: 'consumer.change_password',
    component: loadable(() => import('./components/pages/ChangePassword')),
  },
  {
    url: '/m/password_reset/confirm/:userId/:token/',
    stateName: 'consumer.password_reset_confirm',
    component: loadable(() => import('./components/pages/PasswordResetConfirm')),
  },
  {
    url: '/m/consumer/adjust_tip/:token',
    stateName: ADJUST_TIP,
    component: loadable(() => import('./components/pages/AdjustTip')),
    routes: [
      {
        url: '/m/consumer/adjust_tip/:token/thank_you',
        stateName: ADJUST_TIP_THANK_YOU,
        component: () => null,
      },
      {
        url: '/m/consumer/adjust_tip/:token/default',
        stateName: ADJUST_TIP_DEFAULT,
        component: () => null,
      },
      {
        url: '/m/consumer/adjust_tip/:token/refunded',
        stateName: ADJUST_TIP_REFUNDED,
        component: () => null,
      },
    ],
  },
  // Note that this route is used in Facebook configuration for redirect during FB connect flow
  {
    url: '/m/fb/auth-with-styleseat',
    stateName: 'auth-with-styleseat',
    component: loadable(() => import('./components/provider/AuthWithStyleSeat/AuthWithStyleSeatContainer')),
  },
  {
    stateName: 'responsive.provider.account_email',
    url: ['/provider/:providerId/account/change-email'],
    data: { trackingName: 'Change Email' },
    component: loadable(() => import('./components/provider/settings/ChangeEmail')),
  },
  {
    stateName: 'responsive.provider.checkout_settings',
    url: '/provider/:providerId/settings/checkout',
    component: loadable(() => import('./components/pages/AdjustTax')),
  },
  {
    stateName: PROVIDER_CLIENT_NOTE,
    url: '/provider/:providerId/client/:clientId/notes',
    data: { trackingName: 'pro_addnote_viewed' },
    component: loadable(() => import('./components/pages/provider/AddClientNote')),
  },
  {
    stateName: PRO_CLIENT_LIST,
    url: '/provider/:providerId/clients',
    data: { trackingName: 'P Clients' },
    component: loadable(() => import('./components/pages/provider/client/ProClientListPage')),
    routes: [
      {
        stateName: PRO_CLIENT_VIEW,
        url: '/provider/:providerId/clients/:clientId',
        data: { trackingName: 'P Client' },
        component: loadable(() => import('./components/pages/provider/client/ProClientDetailPage')),
        isChild: true,
        routes: [
          {
            stateName: PRO_CLIENT_VIEW_APPOINTMENT,
            url: '/provider/:providerId/clients/:clientId/appointment/:viewApptId',
            component: loadable(() => import('./components/pages/provider/client/ProClientAppointmentDetailsPage')),
          },
          {
            stateName: PRO_CLIENT_VIEW_APPOINTMENTS,
            url: '/provider/:providerId/clients/:clientId/appointments',
            component: loadable(() => import('./components/pages/provider/client/FutureClientAppointmentsPage')),
          },
        ],
      },
      {
        stateName: PRO_CLIENT_IMPORT,
        url: '/provider/:providerId/clients/import',
        data: { trackingName: 'pro_client_import' },
        component: loadable(() => import('./components/pages/provider/client/ImportContactPage')),
      },
    ],
  },
  {
    stateName: CLIENT_PAYMENT_REQUEST_CHECKOUT,
    url: '/m/payment_request/:token',
    component: loadable(() => import('./components/pages/PaymentRequestCheckout/PaymentRequestCheckout')),
  },
  {
    stateName: TRACKED_CLIENT_SIGNUP,
    url: '/m/client-signup',
    component: loadable(() => import('./components/pages/TrackedSignupPage')),
  },
];

if (isPreStage()) {
  routeConfigs.push({
    url: '/m/errorboundary',
    stateName: 'errorboundary',
    component: loadable(() => import('./components/pages/examples/ErrorBoundaryPage')),
  });
}

if (!isProduction()) {
  routeConfigs.push({
    stateName: 'compare_env_flags_summary',
    url: [
      '/m/compare-env-flags-summary',
      '/m/checkered-flags',
    ],
    component: loadable(() => import('./components/pages/CompareEnvFlagsSummary')),
  });
}

export default routeConfigs;
